import React, { useEffect, useRef, useState } from "react";

export const MetricCounter = ({
  value = 0,
  suffix = "",
  prefix = "",
  duration = 1600,
  className = "",
  active,
  decimals,
}) => {
  const precision =
    decimals ?? Math.max(0, (String(value).split(".")[1] || "").length);
  const [current, setCurrent] = useState(0);
  const ref = useRef(null);
  const playedRef = useRef(false);
  const frameRef = useRef(0);

  useEffect(() => {
    const reduce =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const play = () => {
      if (playedRef.current) return;
      playedRef.current = true;

      if (reduce || duration <= 0) {
        setCurrent(value);
        return;
      }

      const start = performance.now();
      const tick = (now) => {
        const progress = Math.min(1, (now - start) / duration);
        const eased = 1 - Math.pow(1 - progress, 3);
        const nextValue = Number((eased * value).toFixed(precision));
        setCurrent(nextValue);

        if (progress < 1) {
          frameRef.current = window.requestAnimationFrame(tick);
        }
      };

      frameRef.current = window.requestAnimationFrame(tick);
    };

    if (active === true) {
      play();
      return () => {
        if (frameRef.current) window.cancelAnimationFrame(frameRef.current);
      };
    }

    if (active === false) {
      return undefined;
    }

    const element = ref.current;
    if (!element) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) play();
        }
      },
      { threshold: 0.4 }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
      if (frameRef.current) window.cancelAnimationFrame(frameRef.current);
    };
  }, [active, duration, precision, value]);

  return (
    <span ref={ref} className={className}>
      {prefix}
      {current}
      <span>{suffix}</span>
    </span>
  );
};

export default MetricCounter;
